/**
 * NIGHTBOUND - shelter log.
 *
 * A running account of what has happened to the creature. This is the game's
 * memory for the player, and it is the same text the actions returned.
 */

import type { LogEntry } from '../state/GameProvider'
import { cx, Panel, PanelHeading } from './ui'

function timeOf(at: number): string {
  try {
    return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return '--:--'
  }
}

const TONE_DOT: Record<LogEntry['tone'], string> = {
  neutral: 'bg-ash-500',
  good: 'bg-rot-500',
  bad: 'bg-blood-500',
}

export function ShelterLog({ entries, className }: { entries: LogEntry[]; className?: string }) {
  return (
    <Panel className={cx('flex flex-col overflow-hidden', className)}>
      <PanelHeading right={<span className="nb-label text-[8px]">{entries.length} recent</span>}>
        Shelter log
      </PanelHeading>
      {entries.length === 0 ? (
        <p className="p-3 text-xs leading-relaxed text-ash-400">
          Nothing has happened yet. Care for it and the log will fill.
        </p>
      ) : (
        <ol className="nb-scroll-thin max-h-72 flex-1 space-y-2.5 overflow-y-auto p-3">
          {entries.map((entry) => (
            <li key={entry.id} className="nb-emerge flex gap-2.5">
              <span
                aria-hidden
                className={cx('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT[entry.tone])}
              />
              <div className="min-w-0">
                <p className="text-xs leading-snug text-bone-300">{entry.message}</p>
                {entry.detail ? (
                  <p className="mt-0.5 text-[11px] leading-snug text-ash-400">{entry.detail}</p>
                ) : null}
                <p className="nb-numeric mt-0.5 text-[9px] text-ash-500">{timeOf(entry.at)}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  )
}
